import { z } from 'zod';
import { finiteNumberSchema, percentageValueSchema } from './money';

// Two itinerary-level pricing blocks that sit after the last service category
// and before Overall Totals: a fixed-rate **Surcharge** (the commercial team's
// "Excedente") and one or more **Agency Commission** lines.
//
// They are additive lines computed from category aggregates — they never
// multiply an individual service row. Both are evaluated by the engine
// (`buildPricingAdjustments`) and folded into every totals scope, so a stored
// figure, the on-screen footer and the exported quote can never disagree.
//
// The two blocks are NOT symmetric on the cost side (XT-543): a Surcharge is our
// margin and adds nothing to cost, while an Agency Commission is money we pay
// out and adds to cost and price alike. See `adjustmentLineOutputFields`.
//
// Spec: `apps/itineraries/prd/pricing-surcharge-agency-commission.md`.

export const pricingSurchargeScopeSchema = z.enum([
  'ALL_CATEGORIES',
  'SERVICES',
]);

// XT-964 — a Services commission can also be split by category, so each
// category carries its own rate: Experiences, Dining, Transportation, and every
// other service. The four together cover exactly what SERVICES does.
export const PRICING_COMMISSION_SERVICE_CATEGORY_SCOPES = [
  'EXPERIENCES',
  'DINING',
  'TRANSPORTATION',
  'OTHER_SERVICES',
] as const;

export const pricingAgencyCommissionScopeSchema = z.enum([
  'ACCOMMODATION',
  'SERVICES',
  ...PRICING_COMMISSION_SERVICE_CATEGORY_SCOPES,
]);

export const pricingAdjustmentScopeSchema = z.enum([
  'ALL_CATEGORIES',
  'SERVICES',
  'ACCOMMODATION',
  ...PRICING_COMMISSION_SERVICE_CATEGORY_SCOPES,
]);

export const pricingAdjustmentKindSchema = z.enum([
  'surcharge',
  'agencyCommission',
]);

// The rate is stored PER LINE (never read from a global constant at render
// time) so a historical quote keeps the rate it was priced with after the
// product default changes.
const adjustmentLineInputFields = {
  id: z.string().min(1),
  // Percent, not a ratio: 3 = 3%, 7.5 = 7.5%. One representation end-to-end —
  // the UI shows it, the DB stores it, the engine divides by 100.
  percent: percentageValueSchema,
  // `null` = follow the formula. A number = user-fixed Price. Clearing the
  // field in the UI restores `null`; changing scope or percent also clears it
  // (a stale override after a scope change is the main source of pricing
  // errors).
  priceOverride: finiteNumberSchema.nullable(),
};

export const pricingSurchargeLineInputSchema = z.object({
  ...adjustmentLineInputFields,
  scope: pricingSurchargeScopeSchema,
});

export const pricingAgencyCommissionLineInputSchema = z.object({
  ...adjustmentLineInputFields,
  scope: pricingAgencyCommissionScopeSchema,
});

export const pricingAdjustmentsInputSchema = z.object({
  surcharges: z.array(pricingSurchargeLineInputSchema),
  agencyCommissions: z.array(pricingAgencyCommissionLineInputSchema),
});

const adjustmentLineOutputFields = {
  id: z.string().min(1),
  percent: percentageValueSchema,
  // The aggregate the amount was computed from — kept on the line so the
  // number is auditable without re-deriving it.
  base: finiteNumberSchema,
  // The formula result. Never overridable: this is the audit trail of what the
  // engine produced, and the value `price` defaults to. It is NOT what the line
  // adds to the itinerary cost — that is `totalCost`, and for a Surcharge the
  // two deliberately differ.
  formulaAmount: finiteNumberSchema,
  // What this line adds to the itinerary `cost` total (XT-543):
  //   - Surcharge         → always 0. It is our margin, not money we pay out,
  //                         so its whole Price lands in profit instead.
  //   - Agency Commission → `formulaAmount`. We do pay it, so it is a real cost
  //                         and its net effect on profit is 0.
  totalCost: finiteNumberSchema,
  // `priceOverride ?? formulaAmount`.
  price: finiteNumberSchema,
  isPriceOverridden: z.boolean(),
};

export const pricingSurchargeLineOutputSchema = z.object({
  ...adjustmentLineOutputFields,
  kind: z.literal('surcharge'),
  scope: pricingSurchargeScopeSchema,
});

export const pricingAgencyCommissionLineOutputSchema = z.object({
  ...adjustmentLineOutputFields,
  kind: z.literal('agencyCommission'),
  scope: pricingAgencyCommissionScopeSchema,
});

// Σ of the block's lines. `totalCost` sums the lines' cost CONTRIBUTION, so a
// Surcharge block's subtotal is always 0 — that is what the table and the export
// print, and it is deliberate (XT-543).
export const pricingAdjustmentBlockTotalsSchema = z.object({
  totalCost: finiteNumberSchema,
  price: finiteNumberSchema,
});

export const pricingSurchargeBlockOutputSchema = z.object({
  lines: z.array(pricingSurchargeLineOutputSchema),
  totals: pricingAdjustmentBlockTotalsSchema,
});

export const pricingAgencyCommissionBlockOutputSchema = z.object({
  lines: z.array(pricingAgencyCommissionLineOutputSchema),
  totals: pricingAdjustmentBlockTotalsSchema,
});

export const pricingAdjustmentBasesSchema = z.object({
  // Σ Price of every pricing-table row whose category section is Accommodation.
  accommodation: finiteNumberSchema,
  // The same Accommodation rows, INCL VAT (XT-1014). This is the base an
  // Accommodation-scoped Agency Commission grosses up over: the agency's
  // margin on the hotel as the CUSTOMER pays for it. Every other scope keeps
  // its ex-VAT base.
  accommodationWithVat: finiteNumberSchema,
  // Σ Price of every row in every OTHER category — derived by exclusion, so a
  // new content category joins `services` with no code change.
  services: finiteNumberSchema,
  allCategories: finiteNumberSchema,
  // XT-964 — `services` split by category: activities and attractions,
  // dining, transportation, and everything else (by exclusion again).
  experiences: finiteNumberSchema,
  dining: finiteNumberSchema,
  transportation: finiteNumberSchema,
  otherServices: finiteNumberSchema,
});

// The combined block total, relocated onto the items that carry it — keyed by
// the TOP-LEVEL item id (a subitem's price weights its parent, and the parent
// holds the whole family's share).
//
// The Surcharge and the service-scoped commissions are collected from the
// non-Accommodation items: a hotel has to print at the rate the customer can
// look up online, so no such fee may land on one (XT-531). An
// Accommodation-scoped commission is the exception since XT-1014 — it is
// CARRIED by the hotel rows themselves, so an Accommodation anchor appears
// here with its own share.
//
// Published by the engine off the same pass that builds `bases`, so the weights
// and the base can never disagree about what a "service" is. Both the Excel
// export and the Customer Preview read this map (XT-596) — the fee reaches the
// customer as part of each item's price, never as a line of its own.
export const pricingAdjustmentDistributionShareSchema = z.object({
  price: finiteNumberSchema,
  cost: finiteNumberSchema,
});

export const pricingAdjustmentDistributionSchema = z.record(
  z.string().min(1),
  pricingAdjustmentDistributionShareSchema
);

export const pricingAdjustmentsOutputSchema = z.object({
  bases: pricingAdjustmentBasesSchema,
  // Empty whenever there is nothing to spread (`isApplicable: false`, or both
  // block totals are zero).
  distribution: pricingAdjustmentDistributionSchema,
  // The share each OPTIONAL top-level item would carry were it accepted: the
  // blocks evaluated with it in the total (XT-969). Display only — a card
  // prints it so accepting an optional never changes its price; no total,
  // budget or export reads it. Absent when no optional item would carry one.
  optionalDistribution: pricingAdjustmentDistributionSchema.optional(),
  // False when the itinerary holds nothing the fees could be priced on: no
  // non-Accommodation row to collect the Surcharge and the service-scoped
  // commissions from, and no Accommodation row to carry an
  // Accommodation-scoped commission (XT-1014). Both blocks then evaluate to
  // nothing: empty `lines`, zero totals, hidden panels, and no contribution to
  // any totals scope or the persisted version price. Existence is what counts,
  // not amount — a €0 service is a service.
  //
  // The stored `pricing_adjustments` lines are NOT discarded when this is
  // false; they are only inert. An accommodation-only itinerary is a transient
  // starting state, and the lines must price again the moment the itinerary
  // can carry them.
  isApplicable: z.boolean(),
  surcharge: pricingSurchargeBlockOutputSchema,
  agencyCommission: pricingAgencyCommissionBlockOutputSchema,
});

export type PricingSurchargeScope = z.infer<typeof pricingSurchargeScopeSchema>;
export type PricingAgencyCommissionScope = z.infer<
  typeof pricingAgencyCommissionScopeSchema
>;
export type PricingAdjustmentScope = z.infer<
  typeof pricingAdjustmentScopeSchema
>;
export type PricingAdjustmentKind = z.infer<typeof pricingAdjustmentKindSchema>;
export type PricingSurchargeLineInput = z.infer<
  typeof pricingSurchargeLineInputSchema
>;
export type PricingAgencyCommissionLineInput = z.infer<
  typeof pricingAgencyCommissionLineInputSchema
>;
export type PricingAdjustmentsInput = z.infer<
  typeof pricingAdjustmentsInputSchema
>;
export type PricingSurchargeLineOutput = z.infer<
  typeof pricingSurchargeLineOutputSchema
>;
export type PricingAgencyCommissionLineOutput = z.infer<
  typeof pricingAgencyCommissionLineOutputSchema
>;
export type PricingAdjustmentLineOutput =
  | PricingSurchargeLineOutput
  | PricingAgencyCommissionLineOutput;
export type PricingAdjustmentBlockTotals = z.infer<
  typeof pricingAdjustmentBlockTotalsSchema
>;
export type PricingAdjustmentBases = z.infer<
  typeof pricingAdjustmentBasesSchema
>;
export type PricingAdjustmentsOutput = z.infer<
  typeof pricingAdjustmentsOutputSchema
>;
export type PricingAdjustmentDistributionShare = z.infer<
  typeof pricingAdjustmentDistributionShareSchema
>;
export type PricingAdjustmentDistribution = z.infer<
  typeof pricingAdjustmentDistributionSchema
>;

// Product default for a new Surcharge line. Stored on the line at creation
// time; existing lines keep whatever rate they were created with.
export const PRICING_SURCHARGE_DEFAULT_PERCENT = 3;

// Dropdown presets for a new Agency Commission line. Anything else is a
// "Custom…" percent — the persisted shape is identical either way (a single
// decimal percent), so `Custom` is a UI mode, not stored state.
//
// Kept ASCENDING for the dropdown, deliberately decoupled from the default below:
// a new line used to seed `PRESET_PERCENTS[0]`, which silently tied "what the
// dropdown lists first" to "what a new line is worth".
export const PRICING_AGENCY_COMMISSION_PRESET_PERCENTS = [10, 12, 15] as const;

// Product default for a new Agency Commission line — 12%, "the one we use the
// most" (XT-563). Like the Surcharge default it is stored on the line at creation
// time, so existing lines keep their own rate (TCP-PR07-004).
export const PRICING_AGENCY_COMMISSION_DEFAULT_PERCENT = 12;

// A Surcharge and a Services commission are both gross-ups
// (`base / (1 - rate)`), so 100% or more is mathematically undefined and a
// negative rate would refund the agency. Out-of-range percents evaluate to 0
// with a diagnostic instead of dividing by zero or producing a negative amount.
export const isPricingAdjustmentPercentInRange = (percent: number): boolean =>
  Number.isFinite(percent) && percent >= 0 && percent < 100;

// ── Per-organisation rates ───────────────────────────────────────────────────
// The two constants above are the PRODUCT defaults. An organisation may override
// both the set of rates its consultants can pick from and the rate a new line is
// seeded with (`organization.settings.pricingAdjustments` — see
// `packages/shared/src/schemas/organization.ts`).
//
// These resolvers take PLAIN VALUES rather than the settings object so this
// module keeps no dependency on the organisation schema, and so the fallback is
// expressed once instead of at each call site. An unconfigured org resolves to
// exactly the previous hard-coded behaviour.

const sortedUniquePercents = (percents: readonly number[]): number[] =>
  [...new Set(percents.filter((p) => Number.isFinite(p)))].sort(
    (a, b) => a - b
  );

// Options for a Surcharge line's percent picker.
//
// `storedPercent` is UNIONED into the list. The Surcharge picker has no "Custom…"
// escape hatch, so a line priced at a rate the org has since removed from its
// list would otherwise render as some *other* rate while the draft and the engine
// still use the stored one — a silent disagreement on money. Including it keeps
// the displayed rate truthful; the consultant can move off it but never lands on
// a value they did not choose.
export const resolveSurchargePercentOptions = (
  allowed: readonly number[] | undefined,
  storedPercent?: number
): number[] => {
  const base =
    allowed && allowed.length > 0
      ? allowed
      : [PRICING_SURCHARGE_DEFAULT_PERCENT];
  return sortedUniquePercents(
    typeof storedPercent === 'number' ? [...base, storedPercent] : base
  );
};

// Rate a NEW Surcharge line is seeded with. Falls back to the product default,
// and to the lowest allowed rate if the configured default is not selectable
// (a list edited without its default — the schema refuses it, but a blob written
// before that refine existed could still hold it).
export const resolveSurchargeDefaultPercent = (
  defaultPercent: number | undefined,
  allowed?: readonly number[]
): number => {
  const options = resolveSurchargePercentOptions(allowed);
  if (
    typeof defaultPercent === 'number' &&
    isPricingAdjustmentPercentInRange(defaultPercent)
  ) {
    return defaultPercent;
  }
  return options.includes(PRICING_SURCHARGE_DEFAULT_PERCENT)
    ? PRICING_SURCHARGE_DEFAULT_PERCENT
    : (options[0] ?? PRICING_SURCHARGE_DEFAULT_PERCENT);
};

// Presets for an Agency Commission line's picker. No stored-value union here —
// that cell keeps its "Custom…" mode, which already renders any off-list rate.
export const resolveAgencyCommissionPresetPercents = (
  allowed: readonly number[] | undefined
): number[] =>
  sortedUniquePercents(
    allowed && allowed.length > 0
      ? allowed
      : PRICING_AGENCY_COMMISSION_PRESET_PERCENTS
  );

export const resolveAgencyCommissionDefaultPercent = (
  defaultPercent: number | undefined,
  allowed?: readonly number[]
): number => {
  if (
    typeof defaultPercent === 'number' &&
    isPricingAdjustmentPercentInRange(defaultPercent)
  ) {
    return defaultPercent;
  }
  // Mirrors the previous hard-coded seed: the FIRST preset.
  return (
    resolveAgencyCommissionPresetPercents(allowed)[0] ??
    PRICING_AGENCY_COMMISSION_PRESET_PERCENTS[0]
  );
};

export const PRICING_ADJUSTMENT_SCOPE_LABELS: Record<
  PricingAdjustmentScope,
  string
> = {
  ALL_CATEGORIES: 'All Categories',
  SERVICES: 'Services',
  ACCOMMODATION: 'Accommodation',
  EXPERIENCES: 'Experiences',
  DINING: 'Dining',
  TRANSPORTATION: 'Transportation',
  OTHER_SERVICES: 'Other services',
};

export const PRICING_ADJUSTMENT_BLOCK_LABELS: Record<
  PricingAdjustmentKind,
  string
> = {
  surcharge: 'Surcharge',
  agencyCommission: 'Agency Commission',
};

export const createEmptyPricingAdjustmentsOutput =
  (): PricingAdjustmentsOutput => ({
    bases: {
      accommodation: 0,
      accommodationWithVat: 0,
      services: 0,
      allCategories: 0,
      experiences: 0,
      dining: 0,
      transportation: 0,
      otherServices: 0,
    },
    distribution: {},
    // No rows at all, so certainly no service row to carry the fees.
    isApplicable: false,
    surcharge: { lines: [], totals: { totalCost: 0, price: 0 } },
    agencyCommission: { lines: [], totals: { totalCost: 0, price: 0 } },
  });
